import { ALL_PERMISSIONS } from "./permissionsConstants";
import { hasAdminAccess } from "../helpers/finalPermissions";

// Single source of truth for the admin navigation, shared by BOTH the
// internal admin scrollable menu (pages/configuration/sidebar.jsx) and the
// global drawer/side menu (pages/configuration/newDrawer.jsx). Keeping the
// items here guarantees the two surfaces stay in lock-step: every access
// button a user can reach from the internal settings menu is also reachable
// from the drawer, and vice-versa.
//
// The menu is split into two sections that always render in this order:
//   1. SUPER ADMIN — cross-tenant / platform-operator tooling. Only ever
//      shown to a super-admin.
//   2. ADMIN — per-company settings. Role- or permission-gated per row.
//
// Each item is a plain object so it can be rendered with whatever markup a
// given surface needs (the sidebar uses <button> + navigate, the drawer uses
// <Link>). `buildAdminMenu` below resolves visibility + access mode for the
// current user so the rendering code stays dumb.

export const ADMIN_MENU_SECTIONS = {
  SUPER_ADMIN: "superAdmin",
  ADMIN: "admin",
};

// Super-admin section — every row is super-admin only.
export const SUPER_ADMIN_MENU_ITEMS = [
  { key: "all-companies", label: "All Companies Settings", path: "/admin/all-companies-settings", icon: "fa fa-th-large" },
  { key: "organization", label: "Organization Settings", path: "/super-admin", icon: "fa fa-flag" },
  { key: "super-admin-settings", label: "Super-Admin Settings", path: "/super-admin/settings", icon: "fa fa-sliders" },
  { key: "platform-config", label: "Environment Variables", path: "/admin/platform-config", icon: "fa fa-sliders" },
  { key: "custom-workers-admin", label: "Custom Workers (Admin)", path: "/admin/custom-workers", icon: "fa fa-cogs" },
  { key: "server-access", label: "Server Access", path: "/admin/server-access", icon: "fa fa-server" },
  { key: "ai-operations", label: "AI Operations", path: "/admin/ai-operations", icon: "fa fa-terminal" },
  { key: "self-construct", label: "Self-Construction", path: "/admin/self-construct", icon: "fa fa-magic" },
  { key: "automation-pipeline", label: "Automation Pipeline", path: "/automation-pipeline", icon: "fa fa-sitemap" },
  { key: "claude-auth", label: "Claude Authentication", path: "/admin/claude-auth", icon: "fa fa-key" },
  { key: "worker-health", label: "Worker Health", path: "/worker-health", icon: "fa fa-heartbeat" },
  { key: "cost-control", label: "Cost Control", path: "/admin-cost-control", icon: "fa fa-dollar" },
  { key: "affiliations", label: "Affiliations", path: "/admin/affiliations", icon: "fa fa-money" },
  { key: "support-chat", label: "Support Chat", path: "/admin/support-chat", icon: "fa fa-comments" },
  { key: "super-admin-activity", label: "Super-Admin Activity", path: "/super-admin-activity", icon: "fa fa-history" },
  { key: "documentation", label: "Documentation", path: "/admin/documentation", icon: "fa fa-book" },
].map((item) => ({ ...item, section: ADMIN_MENU_SECTIONS.SUPER_ADMIN, superAdminOnly: true }));

// Admin section — per-company. Items carry either a `role` gate (super/company
// admin), a `permission` (renders a Request Access / Acquire License affordance
// when missing), or neither (always visible to anyone who can see the section).
export const ADMIN_MENU_ITEMS = [
  { key: "company-profile", label: "Company Profile", path: "/admin/company-settings", icon: "fa fa-building" },
  { key: "custom-workers", label: "Custom Workers", path: "/custom-workers", icon: "fa fa-magic", role: "admin" },
  { key: "brands-settings", label: "Brands Settings", path: "/admin/brands-settings", icon: "fa fa-shopping-bag", permission: ALL_PERMISSIONS.BRAND_VIEW },
  { key: "hot-topics", label: "Hot Topics", path: "/admin/brands-settings/hot-topics", icon: "fa fa-fire", permission: ALL_PERMISSIONS.HOT_TOPIC_VIEW },
  { key: "statuses", label: "Status", path: "/admin/statuses", icon: "fa fa-flag", permission: ALL_PERMISSIONS.STATUS_VIEW },
  { key: "user-permissions", label: "Users, Roles & Permissions", path: "/admin/user-permissions", icon: "fa fa-users", permission: ALL_PERMISSIONS.USER_VIEW },
  { key: "feature-requests", label: "Feature Access Requests", path: "/admin/feature-requests", icon: "fa fa-lightbulb-o", role: "admin" },
  { key: "alert-preferences", label: "Alert Preferences", path: "/admin/alert-preferences", icon: "fa fa-bell-o", role: "admin" },
  { key: "activity-log", label: "Activity Log", path: "/activity-log", icon: "fa fa-history" },
].map((item) => ({ ...item, section: ADMIN_MENU_SECTIONS.ADMIN }));

// Resolve the menu for the current user. Returns the two sections, each as an
// array of items with a resolved `mode`:
//   - "link"    → navigable
//   - "request" → user lacks the permission; show Request Access / Acquire License
// A `null` section means it has no visible rows and should not render.
//
// The whole admin surface is gated by admin access (hasAdminAccess). A user
// without it — no admin role and no adminSectionAccess grant — sees NEITHER
// section: the admin side menu simply does not render for them, exactly like
// any other feature they aren't entitled to. Only once a user is past that
// gate do the per-row role/permission gates below decide individual rows.
export function buildAdminMenu({ userInfo, finalPermissions } = {}) {
  const isSuperAdmin = !!(userInfo && userInfo.superAdmin);
  const isCompanyAdmin = !!(userInfo && userInfo.companyAdmin);
  const perms = Array.isArray(finalPermissions) ? finalPermissions : [];
  const adminAccess = hasAdminAccess({ userInfo, finalPermissions: perms });
  // Super-admin bypasses every permission gate (mirrors computeFinalPermissions
  // and the backend requireSuper bypass), so every row resolves to a link even
  // if the caller passes an unresolved permission list.
  const has = (p) => isSuperAdmin || perms.includes(p);

  // No admin access → no admin menu at all (both sections suppressed).
  if (!adminAccess) {
    return { superAdmin: null, admin: null };
  }

  const superAdmin = isSuperAdmin
    ? SUPER_ADMIN_MENU_ITEMS.map((item) => ({ ...item, mode: "link" }))
    : [];

  const admin = ADMIN_MENU_ITEMS.reduce((acc, item) => {
    // Role gates: hide entirely when not satisfied.
    if (item.role === "admin" && !(isSuperAdmin || isCompanyAdmin)) return acc;
    if (
      item.role === "automation" &&
      !(isSuperAdmin || has(ALL_PERMISSIONS.AUTOMATION_MANAGE))
    )
      return acc;
    // Sub-items that require a permission just to appear (no request affordance).
    if (item.requires && !has(item.requires)) return acc;
    // Permission items always appear; mode flips to "request" when missing.
    const mode = item.permission && !has(item.permission) ? "request" : "link";
    acc.push({ ...item, mode });
    return acc;
  }, []);

  return {
    superAdmin: superAdmin.length ? superAdmin : null,
    admin: admin.length ? admin : null,
  };
}
